import React, { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { INR } from "@/lib/format";
import { PageHeader } from "@/components/shared";
import { Card } from "@/components/ui/card";
import { Landmark, Loader2, Banknote, FileText, CheckCircle2, TrendingUp } from "lucide-react";
import { useNavigate } from "react-router-dom";

function M({ label, value, tone = "primary", sub, onClick }) {
  const tones = {
    primary: "text-blue-600 bg-blue-50 dark:bg-blue-950/50 dark:text-blue-400",
    emerald: "text-emerald-600 bg-emerald-50 dark:bg-emerald-950/50 dark:text-emerald-400",
    amber: "text-amber-600 bg-amber-50 dark:bg-amber-950/50 dark:text-amber-400",
    violet: "text-violet-600 bg-violet-50 dark:bg-violet-950/50 dark:text-violet-400",
  };
  return (
    <button onClick={onClick} className="text-left rounded-xl border border-border bg-card p-4 hover:shadow-md hover:-translate-y-0.5 transition-all">
      <div className="flex items-start justify-between">
        <span className="text-xs font-medium text-muted-foreground">{label}</span>
        <span className={`h-7 w-7 rounded-lg flex items-center justify-center ${tones[tone]}`}><Banknote className="h-4 w-4" /></span>
      </div>
      <div className="mt-2 text-xl font-extrabold tracking-tight tabular">{value}</div>
      {sub && <div className="text-[11px] text-muted-foreground mt-0.5">{sub}</div>}
    </button>
  );
}

export default function LoanDashboard() {
  const nav = useNavigate();
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/loan/dashboard").then(({ data }) => setD(data)).catch(() => {}); }, []);
  if (!d) return <div className="flex items-center justify-center h-96"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  return (
    <div>
      <PageHeader title="Loan Business" subtitle="Live loan pipeline — requested vs sanctioned vs disbursed kept distinct" icon={Landmark} />
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        <M label="Total Loan Leads" value={d.total_leads} onClick={() => nav("/loan-leads")} />
        <M label="New Leads" value={d.new_leads} tone="violet" onClick={() => nav("/loan-leads")} />
        <M label="Applications Submitted" value={d.applications_submitted} onClick={() => nav("/loan-applications")} />
        <M label="Under Review" value={d.applications_under_review} tone="amber" onClick={() => nav("/loan-applications")} />
        <M label="Lender Approvals" value={d.lender_confirmed_approvals} tone="emerald" onClick={() => nav("/loan-applications")} />
        <M label="Disbursements" value={d.confirmed_disbursements} tone="emerald" onClick={() => nav("/loan-applications")} />
        <M label="Lead → Application" value={`${d.lead_to_application}%`} tone="violet" />
        <M label="Application → Disbursal" value={`${d.application_to_disbursement}%`} tone="violet" />
      </div>
      <h2 className="text-lg font-bold mt-8 mb-4">Loan Amounts (distinct)</h2>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        <M label="Total Requested" value={INR(d.total_requested)} sub="customer ask" />
        <M label="Total Sanctioned" value={INR(d.total_sanctioned)} tone="amber" sub="lender-approved" />
        <M label="Total Disbursed" value={INR(d.total_disbursed)} tone="emerald" sub="actually paid out" />
        <M label="Commission Received" value={INR(d.commission_received)} tone="emerald" onClick={() => nav("/loan-commissions")} />
        <M label="Expected Commission" value={INR(d.expected_commission)} tone="violet" />
        <M label="Confirmed Commission" value={INR(d.confirmed_commission)} tone="primary" />
        <M label="Commission Pending" value={INR(d.commission_pending)} tone="amber" onClick={() => nav("/loan-commissions")} />
        <M label="Docs Pending" value={d.docs_pending} tone="amber" onClick={() => nav("/loan-applications")} />
      </div>
    </div>
  );
}
